import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import type { State } from "@store/reducers";
import { GSGrid, SectionComponent, Pagination, ConfirmationModal } from "@components";
import type { EmployeeAbsenceAlert } from "@modules/shared/reducer/employee-absences/types";
import { formatDateTime } from "@transformers/date";
import {
  getAlertsTriggered,
  getAlertsTriggeredPagination,
} from "@modules/shared/selectors/employee-absences";
import actionCreators from "@modules/shared/actionCreator";
import type { PaginationExtendedType } from "@types";

import { selectUserType } from "@modules/account/selectors";
type OwnProps = {
  id: string,
  clientId: number,
  employeeId: number,
  absenceId: number,
};

type PropsFromState = {
  alertsTriggered: Array<EmployeeAbsenceAlert>,
  pagination: PaginationExtendedType,
  selectUserType: string,
};

type PropsFromDispatch = {
  getAlertsTriggered: typeof actionCreators.GetAlertsTriggered,
  cancelAlert: typeof actionCreators.CancelAlert,
};

type Props = {
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
};

type ComponentState = {
  isSectionOpen: boolean,
};

export function AlertsTriggered(props: Props) {
  const [isSectionOpen, setIsSectionOpen]: [boolean, Function] = useState(true);
  const [isConfirmationDeleteAlertModalVisible, setIsConfirmationDeleteAlertModalVisible]: [
    boolean,
    Function,
  ] = useState(false);

  const [selectedAlert, setSelectedAlert]: [EmployeeAbsenceAlert | null, Function] = useState(null);
  const tableCols = [
    {
      key: "alertId",
      width: "max-content",
      minWidth: 100,
      name: props.t("ALERT_ID"),
      renderCell: ({ row }: { row: EmployeeAbsenceAlert }) => row.id,
    },
    {
      key: "createdAt",
      width: "max-content",
      minWidth: 100,
      name: props.t("DATE_TRIGGERED"),
      renderCell: ({ row }: { row: EmployeeAbsenceAlert }) => formatDateTime(row.createdAt),
    },
    {
      key: "alert",
      align: "left",
      minWidth: 300,
      width: "max-content",
      name: props.t("ALERT_NAME"),
      renderCell: ({ row }: { row: EmployeeAbsenceAlert }) => row.alert.name,
    },
    {
      key: "recipients",
      align: "left",
      name: props.t("RECIPIENTS"),
      renderCell: ({ row }: { row: EmployeeAbsenceAlert }) =>
        row.recipients.map(i => i.name).join(", "),
    },
  ];
  if (["ADMIN", "SERVICE_MANAGER", "CLIENT_ADMIN"].includes(props.selectUserType))
    tableCols.push({
      key: "cancel",
      name: props.t("CANCEL"),
      width: 50,
      onCellClick: (row: EmployeeAbsenceAlert) => handleOnAlertDelete(row),
      renderCell: () => GSGrid.render.materialIcon("cancel", "negative"),
    });

  useEffect(() => {
    props.getAlertsTriggered({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: props.absenceId,
    });
  }, []);

  function handlePageChange(page: number) {
    props.getAlertsTriggered({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: props.absenceId,
      page,
    });
  }

  function handleOnAlertDelete(row: EmployeeAbsenceAlert) {
    toggleConfirmationModal();
    setSelectedAlert(row);
  }

  function toggleConfirmationModal() {
    if (isConfirmationDeleteAlertModalVisible) setSelectedAlert(null);
    setIsConfirmationDeleteAlertModalVisible(!isConfirmationDeleteAlertModalVisible);
  }

  function deleteAlert() {
    props.cancelAlert({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: props.absenceId,
      alertId: selectedAlert.id,
    });
    toggleConfirmationModal();
  }

  function toggleSection() {
    setIsSectionOpen(!isSectionOpen);
  }

  return (
    <SectionComponent
      id={props.id}
      title={<h5>{props.t("ALERTS")}</h5>}
      wrapperClassName="flex-column"
      collapsable
      t={props.t}
      onTitleClick={toggleSection}
      isOpen={isSectionOpen}
      className="gs-container"
    >
      <GSGrid columns={tableCols} rows={props.alertsTriggered} />
      <Pagination
        className="my-3 d-flex flex-fill justify-content-center"
        currentPage={props.pagination.pageNumber}
        totalPages={props.pagination.totalPages}
        onChange={handlePageChange}
      />
      {isConfirmationDeleteAlertModalVisible && (
        <ConfirmationModal
          headerText={props.t("ARE_YOU_SURE")}
          bodyContent={props.t("CANCEL_ALERT_CONFIRMATION_TEXT")}
          confirmBtnLabel={props.t("YES_CANCEL_ALERT")}
          confirmBtnColor="negative"
          cancelBtnLabel={props.t("NO_KEEP_ALERT")}
          cancelBtnColor="primary"
          onConfirm={deleteAlert}
          onCancel={toggleConfirmationModal}
        />
      )}
    </SectionComponent>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  selectUserType: selectUserType(state),
  alertsTriggered: getAlertsTriggered(state),
  pagination: getAlertsTriggeredPagination(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAlertsTriggered: actionCreators.GetAlertsTriggered,
  cancelAlert: actionCreators.CancelAlert,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AlertsTriggered);
